import { Injectable, Injector } from '@angular/core';
import { Cluster, ClusterDragDropLocation } from './cluster.interface';
import { BuildingService } from '../building/building.service';
import { ApiService } from '../../api/api.service';
import { ProjectService } from '../project/project.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ClusterCoreService } from './cluster-core.service';
import { Building } from '../building/building.interface';
import { Observable } from 'rxjs';
import { ModalService } from '../../services/modal/modal.service';
import { ScrollToService } from '../../utilities/scroll-to/scroll-to.service';
import { ColorSchemeColor } from '../../utilities/color/color.interface';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Area } from '../area/area.interface';
import { AreaService } from '../area/area.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Cluster service.
 *
 * The public interface for the ClusterCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class ClusterService extends ClusterCoreService {

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected buildingService: BuildingService,
    protected injector: Injector,
    private modalService: ModalService,
    protected projectService: ProjectService,
    private scrollToService: ScrollToService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super(apiService, apiLegacyService, buildingService, injector, projectService);
  }

  /**
   * Add cluster.
   *
   * @param building The parent building.
   * @param [template={}] Template of the cluster to add.
   * @param [confirm=false] Whether to show the confirm modal.
   */
  public add(building: Building, template: Partial<Cluster> = {}, confirm: boolean = false): void {
    if (confirm) {
      this.modalService.openAddCluster(building).afterClosed().subscribe((cluster: Partial<Cluster>) => {
        if (cluster) {
          this.add(building, cluster);
        }
      });
    } else {
      this._add(building, template).subscribe(response => {
        this.toastService.open(this.translateService.get('clusteradded'));
        this.scrollToService.find(response._id);
      });
    }
  }

  /**
   * Delete cluster.
   *
   * @todo Check if the localpatch is needed with the new api.
   *
   * @param cluster The cluster to delete.
   * @param [confirm=false] Whether to show the confirm modal.
   */
  public delete(cluster: Cluster, confirm: boolean = false): void {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('clusterDeleteTitle'),
        content: this.translateService.get('areyousuredeletingcluster'),
        action: this.translateService.getPlural('delete'),
        actionIcon: 'delete',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(cluster);
        }
      });
    } else {
      this._delete(cluster).subscribe(response => {
        this.toastService.open(this.translateService.get('clusterDeleted'));
      });
    }
  }

  /**
   * Edit cluster.
   *
   * @param building The parent building.
   * @param [template={}] Template of the cluster to add.
   * @param [confirm=false] Whether to show the confirm modal.
   */
  public edit(cluster: Cluster, values: Partial<Cluster>, confirm: boolean = false): void {
    if (confirm) {
      this.modalService.openEditCluster(cluster).afterClosed().subscribe(update => {
        if (update) {
          this.edit(cluster, update);
        }
      });
    } else {
      this._patch(cluster, values).subscribe(response => {
        this.toastService.open(this.translateService.get('clusteredited'));
      });
    }
  }

  /**
   * Get total BUI for a cluster.
   *
   * @param cluster The cluster to get the total BUI for.
   * @returns Total cluster BUI.
   */
  public getBui(cluster: Cluster): number {
    return this._getFunctionalitySurface(cluster, 5);
  }

  /**
   * Get total BVO for a cluster.
   *
   * @param cluster The cluster to get the total BVO for.
   * @returns Total cluster BVO.
   */
  public getBvo(cluster: Cluster): number {
    return this.getFno(cluster) * (cluster.ratio || 0);
  }

  /**
   * Get total FNO for a cluster.
   *
   * @param cluster The cluster to get the total FNO for.
   * @returns Total cluster FNO.
   */
  public getFno(cluster: Cluster): number {
    return this._getFunctionalitySurface(cluster, 1);
  }

  /**
   * Get total NFNO for a cluster.
   *
   * @param cluster The cluster to get the total NFNO for.
   * @returns Total cluster NFNO.
   */
  public getNfno(cluster: Cluster): number {
    return this._getFunctionalitySurface(cluster, 2);
  }

  /**
   * Get total SAN for a cluster.
   *
   * @param cluster The cluster to get the total SAN for.
   * @returns Total cluster SAN.
   */
  public getSan(cluster: Cluster): number {
    return this._getFunctionalitySurface(cluster, 3);
  }

  /**
   * Get total surface for a cluster in square meters.
   *
   * @param cluster The cluster to get the total surface for.
   */
  public getSurface(cluster: Cluster): number {
    return cluster.areas
      .filter(a => a.active)
      .map(a => a.surface * a.quantity)
      .reduce((acc, item) => acc + item, 0);
  }

  /**
   * Get total VER for a cluster.
   *
   * @param cluster The cluster to get the total VER for.
   * @returns Total cluster SAN.
   */
  public getVer(cluster: Cluster): number {
    return this._getFunctionalitySurface(cluster, 4);
  }

  /**
   * Move/sort cluster.
   *
   * @param cluster The cluster to move.
   * @param from The previous index.
   * @param to The target index.
   */
  public move(cluster: Cluster, from: number, to: number): void {

    // Create locally updated cluster array, for calculating positions for the api.
    // Note that it actually performs a reorder on a *reference* to the building.
    const clusters = this.buildingService
      .getBuilding(cluster.buildingId)
      .clusters.filter(c => c.active);
    moveItemInArray(clusters, from, to);

    // Update cluster in the store. Should actually be done after the api returns
    // success, but that created a 'flicker', as the clusters quickly flashes back
    // the old position before jumping to its new position.
    this.projectService.moveCluster(cluster, from, to);

    // Generate object for use with the api.
    let position: ClusterDragDropLocation['position'];
    let target: ClusterDragDropLocation['id'];
    switch (to) {
      case 0:
        position = 'above';
        target = clusters[to + 1]._id;
        break;
      default:
        position = 'below';
        target = clusters[to - 1]._id;
        break;
    }

    this.apiLegacyService.moveCluster(cluster, {
      from: from,
      to: to,
      id: target,
      position: position
    }).subscribe(results => {
      this.toastService.open(this.translateService.get('clusterMoved'));
    });
  }

  /**
   * Set cluster color.
   *
   * @param cluster The cluster to set the color for.
   * @param color The color to set, in hex format.
   */
  public setColor(cluster: Cluster, color: ColorSchemeColor['hex']): void {
    this._patch(cluster, { color: color }).subscribe(response => {
      this.toastService.open(this.translateService.get('clusteredited'));
    });
  }

  /**
   * Set the ratio of the cluster.
   *
   * The ratio will be rounded to three decimals, and the trailing zeros are trimmed.
   *
   * To reduce the number of properties on each entity, only save `ratioAs` and `ratioBy` on
   * building entities, so both the cluster and the parent building should be patched.
   *
   * @todo Combine API calls with Observable.all().
   *
   * @param cluster The cluster to set the ratio for.
   * @param ratio The updated ratio value.
   */
  public setRatio(cluster: Cluster, ratio: Cluster['ratio'], ratioAs: Building['ratioAs']): void {
    const building = this.buildingService.getBuilding(cluster.buildingId);

    this.apiService.updateCluster(cluster, { ratio: ratio }).subscribe(() => {
      this.apiService.updateBuilding(building, { ratioAs: ratioAs, ratioBy: 'cluster' }).subscribe(() => {
        this.projectService.patchCluster(cluster, { ratio: ratio });
        this.toastService.open(this.translateService.get('clusteredited'));
      });
    });
  }

  /**
   * Calculate total surface for a certain functionality.
   *
   * @param cluster The cluster to calculate the total for.
   * @param functionality The functionality to calculate the total for.
   * @returns The total surface of all areas with a certain functionality, within the cluster.
   */
  private _getFunctionalitySurface(cluster: Cluster, functionality: Area['functionality']): number {
    return cluster.areas
      .filter(a => a.active && a.functionality === functionality)
      .map(a => a.surface * a.quantity)
      .reduce((acc, item) => acc + item, 0);
  }
}
